import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { SxProps, Theme } from '@mui/material/styles';
import { imageService } from '../../api/imageService';

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(file);
  });
}

export type DraftHeroBannerProps = {
  imageUrl: string;
  onImageUrlCommitted: (url: string) => void;
  ariaLabel: string;
  /** Max banner height (px). */
  maxHeight?: number;
  sx?: SxProps<Theme>;
  /** When true, uses a red frame until an image is set (no visible label). */
  required?: boolean;
  /** When true, hide URL/upload controls (e.g. draft marked ready to publish). */
  readOnly?: boolean;
};

export function DraftHeroBanner({
  imageUrl,
  onImageUrlCommitted,
  ariaLabel,
  maxHeight = 200,
  sx,
  required = false,
  readOnly = false,
}: DraftHeroBannerProps) {
  const [urlAnchor, setUrlAnchor] = useState<HTMLElement | null>(null);
  const [urlDraft, setUrlDraft] = useState(imageUrl);
  const [busy, setBusy] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const fileInputId = React.useId();

  useEffect(() => {
    setUrlDraft(imageUrl);
  }, [imageUrl]);

  const urlPopoverOpen = Boolean(urlAnchor);

  const applyFromUrl = async () => {
    const url = urlDraft.trim();
    if (!url) {
      setBannerError('Enter an image URL.');
      return;
    }
    setBusy(true);
    setBannerError(null);
    try {
      const res = await imageService.ingestOgImage({ url });
      onImageUrlCommitted(res.url);
      setUrlDraft(res.url);
      setUrlAnchor(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setBannerError(`${msg} Using the URL directly.`);
      onImageUrlCommitted(url);
      setUrlDraft(url);
      setUrlAnchor(null);
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setBannerError(null);
    try {
      const res = await imageService.ingestOgImage({ file });
      onImageUrlCommitted(res.url);
      setUrlDraft(res.url);
    } catch {
      try {
        const dataUrl = await readFileAsDataUrl(file);
        onImageUrlCommitted(dataUrl);
        setUrlDraft(dataUrl);
        setBannerError(null);
      } catch {
        setBannerError('Upload failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fafafa',
      '& fieldset': { borderColor: '#3f3f46' },
      '&:hover fieldset': { borderColor: '#a1a1aa' },
      '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
    },
    '& .MuiInputLabel-root': { color: '#a1a1aa' },
  };

  const displayUrl = (imageUrl || '').trim();
  const needsRequired = required && !displayUrl;

  const openUrlPopover = (el: HTMLElement) => {
    setUrlAnchor(el);
    setUrlDraft(imageUrl);
    setBannerError(null);
  };

  const controlBtnSx = {
    bgcolor: 'rgba(0,0,0,0.55)',
    color: '#fafafa',
    border: '1px solid #3f3f46',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.72)' },
    '&.Mui-disabled': { opacity: 0.5 },
  };

  return (
    <Box sx={[{ mb: 2 }, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]} aria-label={ariaLabel}>
      <Box
        role={required ? 'group' : undefined}
        aria-required={required || undefined}
        sx={{
          position: 'relative',
          width: '100%',
          maxHeight,
          minHeight: displayUrl ? maxHeight : Math.min(120, maxHeight),
          borderRadius: 1,
          overflow: 'hidden',
          border: needsRequired ? '1px solid #f87171' : '1px solid #27272a',
          bgcolor: displayUrl ? '#0a0a0a' : '#171717',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {displayUrl ? (
          <Box
            component="img"
            src={displayUrl}
            alt=""
            sx={{ width: '100%', height: '100%', maxHeight, objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
          />
        ) : (
          <Typography variant="body2" sx={{ color: '#52525b', px: 2, textAlign: 'center', pr: 10 }}>
            No image yet
          </Typography>
        )}

        {!readOnly ? (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              alignItems: 'center',
            }}
          >
            <Tooltip title="Set image from URL">
              <IconButton
                type="button"
                size="small"
                disabled={busy}
                onClick={(e) => openUrlPopover(e.currentTarget)}
                aria-label={`Image URL for ${ariaLabel}`}
                sx={controlBtnSx}
              >
                <InsertLinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Upload from computer">
              <Box
                component="label"
                htmlFor={fileInputId}
                sx={{
                  ...controlBtnSx,
                  display: 'inline-flex',
                  width: 34,
                  height: 34,
                  borderRadius: 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.5 : 1,
                  pointerEvents: busy ? 'none' : 'auto',
                }}
              >
                <UploadFileIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
            <input id={fileInputId} type="file" accept="image/*" hidden onChange={handleFile} />
          </Stack>
        ) : null}
      </Box>

      {bannerError && !urlPopoverOpen ? (
        <Alert
          severity="info"
          onClose={() => setBannerError(null)}
          sx={{ mt: 1, bgcolor: '#27272a', color: '#fafafa' }}
        >
          {bannerError}
        </Alert>
      ) : null}

      <Popover
        open={urlPopoverOpen}
        anchorEl={urlAnchor}
        onClose={() => {
          setUrlAnchor(null);
          setBannerError(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { bgcolor: '#171717', border: '1px solid #27272a', p: 2, minWidth: 280, maxWidth: 360 },
          },
        }}
      >
        {bannerError && (
          <Alert severity="info" sx={{ mb: 1, bgcolor: '#27272a', color: '#fafafa' }}>
            {bannerError}
          </Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            label="Image URL"
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void applyFromUrl();
              }
            }}
            sx={fieldSx}
          />
          <Tooltip title="Apply URL (server stores when available)">
            <span>
              <IconButton
                type="button"
                size="small"
                disabled={busy}
                onClick={() => void applyFromUrl()}
                sx={{ color: '#a1a1aa', border: '1px solid #3f3f46', mt: 0.25 }}
              >
                {busy ? <CircularProgress size={20} color="inherit" /> : <InsertLinkIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Popover>
    </Box>
  );
}
